import { collectBrowserInfo } from '@supply-mover-2-0/core';
import type {
	PortalCardFundingRequestDto,
	PortalCardFundingResponseDto,
	PortalCreatePaymentMethodDto,
	PortalCyberSourceCaptureContextRequestDto,
	PortalCyberSourceCaptureContextResponseDto,
	PortalGatewayConfigResponseDto,
	PortalMakePaymentContextResponseDto,
	PortalMakePaymentDto,
	PortalMakePaymentResponseDto,
	PortalPayAccountsContextResponseDto,
	PortalPayAccountsDto,
	PortalPayAccountsResponseDto,
	PortalPaymentAvailabilityResponseDto,
	PortalPaymentMethodListResponseDto,
	PortalPaymentMethodResponseDto,
	PortalSupportedPaymentTypesResponseDto,
} from '@supply-mover-2-0/shared';
import { portalFetch } from '../lib/apiClient';

/**
 * PD-16789 — whether to show Make a Payment: invoice payments on, pricing
 * visible, a gateway and a card or bank type offered, and something to pay
 * on any of the user's accounts (or Account Funds deposits allowed).
 */
export async function getPaymentAvailability(
	token: string,
): Promise<PortalPaymentAvailabilityResponseDto> {
	return portalFetch<PortalPaymentAvailabilityResponseDto>(
		'/payments/availability',
		token,
	);
}

/**
 * PD-14057 — everything the Make Payment screen needs in one call:
 * open invoices, account funds, allowed payment types, saved methods.
 */
export async function getMakePaymentContext(
	token: string,
): Promise<PortalMakePaymentContextResponseDto> {
	return portalFetch<PortalMakePaymentContextResponseDto>(
		'/payments/make-payment-context',
		token,
	);
}

/**
 * PD-14057 — pay one or more invoices (with an optional explicit
 * on-account remainder) via saved method or Account Funds.
 */
export async function makePayment(
	token: string,
	payload: PortalMakePaymentDto,
): Promise<PortalMakePaymentResponseDto> {
	return portalFetch<PortalMakePaymentResponseDto>(
		'/payments/make-payment',
		token,
		{
			method: 'POST',
			// Browser snapshot attached here so every caller gets gateway risk
			// scoring data without wiring it per-screen (PD-15229).
			body: JSON.stringify({
				...payload,
				browserInfo: payload.browserInfo ?? collectBrowserInfo() ?? undefined,
			}),
		},
	);
}

/**
 * PD-14190 — context for the multi-account "pay across my accounts" screen:
 * open invoices grouped by every managed account (each with its own funds +
 * methods for a per-account override) plus the payer's default methods.
 */
export async function getPayAccountsContext(
	token: string,
): Promise<PortalPayAccountsContextResponseDto> {
	return portalFetch<PortalPayAccountsContextResponseDto>(
		'/payments/pay-accounts-context',
		token,
	);
}

/**
 * PD-14190 — pay open invoices across multiple managed accounts in one action
 * (fan-out: one payment per account). Returns a per-account result.
 */
export async function payAccounts(
	token: string,
	payload: PortalPayAccountsDto,
): Promise<PortalPayAccountsResponseDto> {
	return portalFetch<PortalPayAccountsResponseDto>(
		'/payments/pay-accounts',
		token,
		{
			method: 'POST',
			// Browser snapshot attached here so every caller gets gateway risk
			// scoring data without wiring it per-screen (PD-15229).
			body: JSON.stringify({
				...payload,
				browserInfo: payload.browserInfo ?? collectBrowserInfo() ?? undefined,
			}),
		},
	);
}

/**
 * Fetch public gateway configuration for client-side tokenization.
 * Returns processor type, environment, and non-sensitive public credentials.
 *
 * PD-16733 — pass `invoiceId` when the form is opened from an invoice's Pay
 * panel: the method may then be saved to the invoice's account, whose
 * processor can differ from the signed-in account's.
 */
export async function getGatewayConfig(
	token: string,
	invoiceId?: number,
): Promise<PortalGatewayConfigResponseDto> {
	const params = invoiceId != null ? `?invoiceId=${invoiceId}` : '';
	return portalFetch<PortalGatewayConfigResponseDto>(
		`/payments/gateway-config${params}`,
		token,
	);
}

/**
 * PD-16381 — whether the card being entered is credit, debit or prepaid,
 * from its BIN, before it is saved. POSTed so the BIN rides in the body
 * rather than the URL. `unknown` means say nothing.
 */
export async function getCardFunding(
	token: string,
	bin: string,
	invoiceId?: number,
): Promise<PortalCardFundingResponseDto> {
	const body: PortalCardFundingRequestDto = { bin, invoiceId };
	return portalFetch<PortalCardFundingResponseDto>(
		'/payments/card-funding',
		token,
		{
			method: 'POST',
			body: JSON.stringify(body),
		},
	);
}

/**
 * PD-16526 — the capture context CyberSource's hosted card or bank account
 * fields need, signed for this page's origin and the active account's
 * merchant account. Returns the context itself so it can be handed straight
 * to `CyberSourceFrontendService.initialize` as its capture-context provider.
 */
export async function getCyberSourceCaptureContext(
	token: string,
	paymentType: PortalCyberSourceCaptureContextRequestDto['paymentType'],
	invoiceId?: number,
): Promise<string> {
	const body: PortalCyberSourceCaptureContextRequestDto = {
		paymentType,
		targetOrigin: window.location.origin,
		invoiceId,
	};
	const response =
		await portalFetch<PortalCyberSourceCaptureContextResponseDto>(
			'/payments/cybersource/capture-context',
			token,
			{
				method: 'POST',
				body: JSON.stringify(body),
			},
		);

	return response.data.captureContext;
}

/**
 * Fetch supported payment method types for the active client.
 * Filtered by the client's payment config and optionally by shipping state.
 */
export async function getSupportedTypes(
	token: string,
	shippingAddressId?: number,
): Promise<PortalSupportedPaymentTypesResponseDto> {
	const params =
		shippingAddressId != null ? `?shippingAddressId=${shippingAddressId}` : '';
	return portalFetch<PortalSupportedPaymentTypesResponseDto>(
		`/payments/supported-types${params}`,
		token,
	);
}

/**
 * Fetch saved payment methods for the active client.
 * Returns masked card/account info — never full numbers.
 */
export async function getPaymentMethods(
	token: string,
): Promise<PortalPaymentMethodListResponseDto> {
	return portalFetch<PortalPaymentMethodListResponseDto>(
		'/payments/methods',
		token,
	);
}

/**
 * Create a new payment method using tokenized opaque data.
 * Raw card/account numbers never reach the server — only tokenized values.
 */
export async function createPaymentMethod(
	token: string,
	payload: PortalCreatePaymentMethodDto,
): Promise<PortalPaymentMethodResponseDto> {
	return portalFetch<PortalPaymentMethodResponseDto>(
		'/payments/methods',
		token,
		{
			method: 'POST',
			body: JSON.stringify(payload),
		},
	);
}

/**
 * Set a payment method as the primary for the active client.
 * The server unsets any other primary method automatically.
 */
export async function setPrimaryPaymentMethod(
	token: string,
	methodId: number,
): Promise<{ success: boolean }> {
	return portalFetch<{ success: boolean }>(
		`/payments/methods/${methodId}/primary`,
		token,
		{ method: 'PUT' },
	);
}

/**
 * Delete a saved payment method. The server verifies ownership
 * against the active client before proceeding.
 */
export async function deletePaymentMethod(
	token: string,
	methodId: number,
): Promise<{ success: boolean }> {
	return portalFetch<{ success: boolean }>(
		`/payments/methods/${methodId}`,
		token,
		{ method: 'DELETE' },
	);
}
